import { createContext, useCallback, useMemo, useState } from 'react';

export const TOOLBAR_LABEL_MODES = {
  ICON: 'icon',
  ICON_AND_TEXT: 'icon-and-text',
};

export const IFRAME_DARK_STRATEGIES = {
  ADAPTIVE: 'adaptive',
  FORCE_DARK: 'force-dark',
};

/** Print/PDF ink darkness for HTML notes (overrides pale dark-mode greys when printing). */
export const PRINT_INK_LEVELS = {
  PALE: 'pale',
  MEDIUM: 'medium',
  DARK: 'dark',
  BLACK: 'black',
};

export const PRINT_INK_OPTIONS = [
  {
    id: PRINT_INK_LEVELS.PALE,
    label: 'Pale',
    description: 'Light grey text on white paper.',
    ink: '#64748b',
    muted: '#94a3b8',
    border: '#d1d5db',
  },
  {
    id: PRINT_INK_LEVELS.MEDIUM,
    label: 'Medium',
    description: 'Medium grey text on white paper.',
    ink: '#334155',
    muted: '#64748b',
    border: '#d1d5db',
  },
  {
    id: PRINT_INK_LEVELS.DARK,
    label: 'Dark',
    description: 'Near-black text on white paper.',
    ink: '#0f172a',
    muted: '#334155',
    border: '#d1d5db',
  },
  {
    id: PRINT_INK_LEVELS.BLACK,
    label: 'Black',
    description: 'Black text on white paper (recommended).',
    ink: '#000000',
    muted: '#1e293b',
    border: '#d1d5db',
  },
];

export const TOOLBAR_LABEL_STORAGE_KEY = 'mywork-viewer-toolbar-labels';
export const IFRAME_DARK_STRATEGY_STORAGE_KEY = 'mywork-iframe-dark-strategy';
export const PRINT_INK_STORAGE_KEY = 'mywork-print-ink-level';
export const ALWAYS_MOBILE_STORAGE_KEY = 'mywork-always-mobile';
export const MOBILE_ZOOM_PERCENT_STORAGE_KEY = 'mywork-mobile-zoom-percent';
export const MOBILE_PAGE_WIDTH_PERCENT_STORAGE_KEY = 'mywork-mobile-page-width-percent';
export const CONTENT_PADDING_STORAGE_KEY = 'mywork-viewer-content-padding';
export const AUTO_SVG_POPUP_STORAGE_KEY = 'mywork-auto-svg-popup';
export const SECTION_EXPAND_ANIMATION_STORAGE_KEY = 'mywork-section-expand-animation';
export const MARKING_SCHEME_STEP_CONTROL_STORAGE_KEY = 'mywork-marking-scheme-step-control';
export const MARKING_SCHEME_ANIMATION_SPEED_STORAGE_KEY = 'mywork-marking-scheme-animation-speed';
export const SVG_ANIMATION_SPEED_STORAGE_KEY = 'mywork-svg-animation-speed';

/** Shared speed choices for Marking Scheme section reveal and SVG sketch play. */
export const ANIMATION_SPEED_OPTIONS = [
  { value: 0.05, label: '0.05×' },
  { value: 0.1, label: '0.1×' },
  { value: 0.2, label: '0.2×' },
  { value: 0.4, label: '0.4×' },
  { value: 0.5, label: '0.5×' },
  { value: 0.75, label: '0.75×' },
  { value: 1, label: '1×' },
  { value: 1.5, label: '1.5×' },
  { value: 2, label: '2×' },
  { value: 5, label: '5×' },
];
export const ANIMATION_SPEED_DEFAULT = 1;

export function clampAnimationSpeed(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return ANIMATION_SPEED_DEFAULT;
  const match = ANIMATION_SPEED_OPTIONS.find((option) => option.value === numeric);
  return match ? match.value : ANIMATION_SPEED_DEFAULT;
}

/** HTML lesson / ExamRight content padding relative to the authored layout. */
export const CONTENT_PADDING_LEVELS = {
  ORIGINAL: 'original',
  COMFORTABLE: 'comfortable',
  COMPACT: 'compact',
  TIGHT: 'tight',
};

export const CONTENT_PADDING_OPTIONS = [
  {
    id: CONTENT_PADDING_LEVELS.ORIGINAL,
    label: 'Original',
    scale: 1,
    description: 'Full authored padding and margins around the page content.',
  },
  {
    id: CONTENT_PADDING_LEVELS.COMFORTABLE,
    label: 'Comfortable',
    scale: 0.75,
    description: 'Slightly tighter spacing while keeping room to breathe.',
  },
  {
    id: CONTENT_PADDING_LEVELS.COMPACT,
    label: 'Compact',
    scale: 0.5,
    description: 'Half padding — recommended for phones and small screens.',
  },
  {
    id: CONTENT_PADDING_LEVELS.TIGHT,
    label: 'Tight',
    scale: 0.25,
    description: 'Minimal padding to maximize content on narrow screens.',
  },
];

/** Matches the HTML viewer zoom range (50%–200%, steps of 10). 70% is the Mobile button. */
export const MOBILE_ZOOM_PERCENT_MIN = 50;
export const MOBILE_ZOOM_PERCENT_MAX = 200;
export const MOBILE_ZOOM_PERCENT_STEP = 10;
export const MOBILE_ZOOM_PERCENT_DEFAULT = 70;

/** Page width as a percentage of the viewer. 100% fills the viewer width. */
export const MOBILE_PAGE_WIDTH_PERCENT_MIN = 50;
export const MOBILE_PAGE_WIDTH_PERCENT_MAX = 200;
export const MOBILE_PAGE_WIDTH_PERCENT_STEP = 10;
export const MOBILE_PAGE_WIDTH_PERCENT_DEFAULT = 100;

export function clampMobileZoomPercent(value) {
  if (value == null || value === '') return MOBILE_ZOOM_PERCENT_DEFAULT;
  const stepped = Math.round(Number(value) / MOBILE_ZOOM_PERCENT_STEP) * MOBILE_ZOOM_PERCENT_STEP;
  if (!Number.isFinite(stepped)) return MOBILE_ZOOM_PERCENT_DEFAULT;
  return Math.min(MOBILE_ZOOM_PERCENT_MAX, Math.max(MOBILE_ZOOM_PERCENT_MIN, stepped));
}

export const ViewerToolbarContext = createContext(null);

export function readStoredToolbarLabelMode() {
  if (typeof window === 'undefined') return TOOLBAR_LABEL_MODES.ICON_AND_TEXT;
  const stored = localStorage.getItem(TOOLBAR_LABEL_STORAGE_KEY);
  if (stored === TOOLBAR_LABEL_MODES.ICON || stored === TOOLBAR_LABEL_MODES.ICON_AND_TEXT) {
    return stored;
  }
  return TOOLBAR_LABEL_MODES.ICON_AND_TEXT;
}

export function readStoredIframeDarkStrategy() {
  if (typeof window === 'undefined') return IFRAME_DARK_STRATEGIES.ADAPTIVE;
  const stored = localStorage.getItem(IFRAME_DARK_STRATEGY_STORAGE_KEY);
  if (stored === IFRAME_DARK_STRATEGIES.ADAPTIVE || stored === IFRAME_DARK_STRATEGIES.FORCE_DARK) {
    return stored;
  }
  return IFRAME_DARK_STRATEGIES.ADAPTIVE;
}

export function readStoredPrintInkLevel() {
  if (typeof window === 'undefined') return PRINT_INK_LEVELS.BLACK;
  const stored = localStorage.getItem(PRINT_INK_STORAGE_KEY);
  if (PRINT_INK_OPTIONS.some((option) => option.id === stored)) {
    return stored;
  }
  return PRINT_INK_LEVELS.BLACK;
}

export function readStoredAlwaysMobile() {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(ALWAYS_MOBILE_STORAGE_KEY) === '1';
}

export function readStoredMobileZoomPercent() {
  if (typeof window === 'undefined') return MOBILE_ZOOM_PERCENT_DEFAULT;
  return clampMobileZoomPercent(localStorage.getItem(MOBILE_ZOOM_PERCENT_STORAGE_KEY));
}

export function clampMobilePageWidthPercent(value) {
  if (value == null || value === '') return MOBILE_PAGE_WIDTH_PERCENT_DEFAULT;
  const stepped =
    Math.round(Number(value) / MOBILE_PAGE_WIDTH_PERCENT_STEP) * MOBILE_PAGE_WIDTH_PERCENT_STEP;
  if (!Number.isFinite(stepped)) return MOBILE_PAGE_WIDTH_PERCENT_DEFAULT;
  return Math.min(
    MOBILE_PAGE_WIDTH_PERCENT_MAX,
    Math.max(MOBILE_PAGE_WIDTH_PERCENT_MIN, stepped)
  );
}

export function readStoredMobilePageWidthPercent() {
  if (typeof window === 'undefined') return MOBILE_PAGE_WIDTH_PERCENT_DEFAULT;
  return clampMobilePageWidthPercent(localStorage.getItem(MOBILE_PAGE_WIDTH_PERCENT_STORAGE_KEY));
}

export function getContentPaddingOption(level) {
  return (
    CONTENT_PADDING_OPTIONS.find((option) => option.id === level) ||
    CONTENT_PADDING_OPTIONS.find((option) => option.id === CONTENT_PADDING_LEVELS.COMPACT)
  );
}

export function getContentPaddingScale(level) {
  return getContentPaddingOption(level).scale;
}

export function readStoredContentPaddingLevel() {
  if (typeof window === 'undefined') return CONTENT_PADDING_LEVELS.COMPACT;
  const stored = localStorage.getItem(CONTENT_PADDING_STORAGE_KEY);
  if (CONTENT_PADDING_OPTIONS.some((option) => option.id === stored)) {
    return stored;
  }
  return CONTENT_PADDING_LEVELS.COMPACT;
}

/** Default on: Marking Scheme opens with the SVG companion when available. */
export function readStoredAutoSvgPopup() {
  if (typeof window === 'undefined') return true;
  const stored = localStorage.getItem(AUTO_SVG_POPUP_STORAGE_KEY);
  if (stored === '0') return false;
  if (stored === '1') return true;
  return true;
}

const MARKING_SCHEME_ANIM_DEFAULTS_MIGRATION_KEY = 'mywork-ms-anim-defaults-v2';

/** One-time: auto animation ON, step control OFF — see lines animate, never block on MS. */
function ensureMarkingSchemeAnimationDefaultsMigrated() {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(MARKING_SCHEME_ANIM_DEFAULTS_MIGRATION_KEY)) return;
  localStorage.setItem(MARKING_SCHEME_ANIM_DEFAULTS_MIGRATION_KEY, '1');
  localStorage.setItem(MARKING_SCHEME_STEP_CONTROL_STORAGE_KEY, '0');
  localStorage.setItem(SECTION_EXPAND_ANIMATION_STORAGE_KEY, '1');
}

/** Default on: expanding a Marking Scheme section auto-plays content lines. */
export function readStoredSectionExpandAnimation() {
  if (typeof window === 'undefined') return true;
  ensureMarkingSchemeAnimationDefaultsMigrated();
  const stored = localStorage.getItem(SECTION_EXPAND_ANIMATION_STORAGE_KEY);
  if (stored === '0') return false;
  if (stored === '1') return true;
  return true;
}

/**
 * Default off: show section line animation automatically first.
 * Step control (MS click / blocking hide-until-click) is opt-in.
 */
export function readStoredMarkingSchemeStepControl() {
  if (typeof window === 'undefined') return false;
  ensureMarkingSchemeAnimationDefaultsMigrated();
  const stored = localStorage.getItem(MARKING_SCHEME_STEP_CONTROL_STORAGE_KEY);
  if (stored === '0') return false;
  if (stored === '1') return true;
  return false;
}

export function readStoredMarkingSchemeAnimationSpeed() {
  if (typeof window === 'undefined') return ANIMATION_SPEED_DEFAULT;
  return clampAnimationSpeed(localStorage.getItem(MARKING_SCHEME_ANIMATION_SPEED_STORAGE_KEY));
}

export function readStoredSvgAnimationSpeed() {
  if (typeof window === 'undefined') return ANIMATION_SPEED_DEFAULT;
  return clampAnimationSpeed(localStorage.getItem(SVG_ANIMATION_SPEED_STORAGE_KEY));
}

export function getPrintInkColors(level) {
  return (
    PRINT_INK_OPTIONS.find((option) => option.id === level) ||
    PRINT_INK_OPTIONS.find((option) => option.id === PRINT_INK_LEVELS.DARK)
  );
}

export function ViewerToolbarProvider({ children }) {
  const [toolbarLabelMode, setToolbarLabelModeState] = useState(readStoredToolbarLabelMode);
  const [iframeDarkStrategy, setIframeDarkStrategyState] = useState(readStoredIframeDarkStrategy);
  const [printInkLevel, setPrintInkLevelState] = useState(readStoredPrintInkLevel);
  const [alwaysMobile, setAlwaysMobileState] = useState(readStoredAlwaysMobile);
  const [mobileZoomPercent, setMobileZoomPercentState] = useState(readStoredMobileZoomPercent);
  const [mobilePageWidthPercent, setMobilePageWidthPercentState] = useState(
    readStoredMobilePageWidthPercent
  );
  const [contentPaddingLevel, setContentPaddingLevelState] = useState(
    readStoredContentPaddingLevel
  );
  const [autoSvgPopup, setAutoSvgPopupState] = useState(readStoredAutoSvgPopup);
  const [sectionExpandAnimation, setSectionExpandAnimationState] = useState(
    readStoredSectionExpandAnimation
  );
  const [markingSchemeStepControl, setMarkingSchemeStepControlState] = useState(
    readStoredMarkingSchemeStepControl
  );
  const [markingSchemeAnimationSpeed, setMarkingSchemeAnimationSpeedState] = useState(
    readStoredMarkingSchemeAnimationSpeed
  );
  const [svgAnimationSpeed, setSvgAnimationSpeedState] = useState(readStoredSvgAnimationSpeed);

  const setToolbarLabelMode = useCallback((mode) => {
    if (mode !== TOOLBAR_LABEL_MODES.ICON && mode !== TOOLBAR_LABEL_MODES.ICON_AND_TEXT) {
      return;
    }
    localStorage.setItem(TOOLBAR_LABEL_STORAGE_KEY, mode);
    setToolbarLabelModeState(mode);
  }, []);

  const setIframeDarkStrategy = useCallback((strategy) => {
    if (strategy !== IFRAME_DARK_STRATEGIES.ADAPTIVE && strategy !== IFRAME_DARK_STRATEGIES.FORCE_DARK) {
      return;
    }
    localStorage.setItem(IFRAME_DARK_STRATEGY_STORAGE_KEY, strategy);
    setIframeDarkStrategyState(strategy);
  }, []);

  const setPrintInkLevel = useCallback((level) => {
    if (!PRINT_INK_OPTIONS.some((option) => option.id === level)) {
      return;
    }
    localStorage.setItem(PRINT_INK_STORAGE_KEY, level);
    setPrintInkLevelState(level);
  }, []);

  const setAlwaysMobile = useCallback((enabled) => {
    const next = Boolean(enabled);
    localStorage.setItem(ALWAYS_MOBILE_STORAGE_KEY, next ? '1' : '0');
    setAlwaysMobileState(next);
  }, []);

  const setMobileZoomPercent = useCallback((percent) => {
    const next = clampMobileZoomPercent(percent);
    localStorage.setItem(MOBILE_ZOOM_PERCENT_STORAGE_KEY, String(next));
    setMobileZoomPercentState(next);
  }, []);

  const setMobilePageWidthPercent = useCallback((percent) => {
    const next = clampMobilePageWidthPercent(percent);
    localStorage.setItem(MOBILE_PAGE_WIDTH_PERCENT_STORAGE_KEY, String(next));
    setMobilePageWidthPercentState(next);
  }, []);

  const setContentPaddingLevel = useCallback((level) => {
    if (!CONTENT_PADDING_OPTIONS.some((option) => option.id === level)) {
      return;
    }
    localStorage.setItem(CONTENT_PADDING_STORAGE_KEY, level);
    setContentPaddingLevelState(level);
  }, []);

  const setAutoSvgPopup = useCallback((enabled) => {
    const next = Boolean(enabled);
    localStorage.setItem(AUTO_SVG_POPUP_STORAGE_KEY, next ? '1' : '0');
    setAutoSvgPopupState(next);
  }, []);

  const setSectionExpandAnimation = useCallback((enabled) => {
    const next = Boolean(enabled);
    localStorage.setItem(SECTION_EXPAND_ANIMATION_STORAGE_KEY, next ? '1' : '0');
    setSectionExpandAnimationState(next);
  }, []);

  const setMarkingSchemeStepControl = useCallback((enabled) => {
    const next = Boolean(enabled);
    localStorage.setItem(MARKING_SCHEME_STEP_CONTROL_STORAGE_KEY, next ? '1' : '0');
    setMarkingSchemeStepControlState(next);
  }, []);

  const setMarkingSchemeAnimationSpeed = useCallback((speed) => {
    const next = clampAnimationSpeed(speed);
    localStorage.setItem(MARKING_SCHEME_ANIMATION_SPEED_STORAGE_KEY, String(next));
    setMarkingSchemeAnimationSpeedState(next);
  }, []);

  const setSvgAnimationSpeed = useCallback((speed) => {
    const next = clampAnimationSpeed(speed);
    localStorage.setItem(SVG_ANIMATION_SPEED_STORAGE_KEY, String(next));
    setSvgAnimationSpeedState(next);
  }, []);

  const value = useMemo(
    () => ({
      toolbarLabelMode,
      setToolbarLabelMode,
      showToolbarLabels: toolbarLabelMode === TOOLBAR_LABEL_MODES.ICON_AND_TEXT,
      iframeDarkStrategy,
      setIframeDarkStrategy,
      printInkLevel,
      setPrintInkLevel,
      printInkColors: getPrintInkColors(printInkLevel),
      alwaysMobile,
      setAlwaysMobile,
      mobileZoomPercent,
      setMobileZoomPercent,
      mobilePageWidthPercent,
      setMobilePageWidthPercent,
      contentPaddingLevel,
      setContentPaddingLevel,
      contentPaddingScale: getContentPaddingScale(contentPaddingLevel),
      autoSvgPopup,
      setAutoSvgPopup,
      sectionExpandAnimation,
      setSectionExpandAnimation,
      markingSchemeStepControl,
      setMarkingSchemeStepControl,
      markingSchemeAnimationSpeed,
      setMarkingSchemeAnimationSpeed,
      svgAnimationSpeed,
      setSvgAnimationSpeed,
    }),
    [
      toolbarLabelMode,
      setToolbarLabelMode,
      iframeDarkStrategy,
      setIframeDarkStrategy,
      printInkLevel,
      setPrintInkLevel,
      alwaysMobile,
      setAlwaysMobile,
      mobileZoomPercent,
      setMobileZoomPercent,
      mobilePageWidthPercent,
      setMobilePageWidthPercent,
      contentPaddingLevel,
      setContentPaddingLevel,
      autoSvgPopup,
      setAutoSvgPopup,
      sectionExpandAnimation,
      setSectionExpandAnimation,
      markingSchemeStepControl,
      setMarkingSchemeStepControl,
      markingSchemeAnimationSpeed,
      setMarkingSchemeAnimationSpeed,
      svgAnimationSpeed,
      setSvgAnimationSpeed,
    ]
  );

  return <ViewerToolbarContext.Provider value={value}>{children}</ViewerToolbarContext.Provider>;
}
